import { BufferHelper } from '../hlsJsExtract/buffer-helper';
import { findFragmentByPTS } from '../hlsJsExtract/fragment-finders';

import { ISubtitleStreamController, State, FragmentState, PlaylistLevelType } from '../IHlsJsExtended';
import { HlsPlayer } from '../HlsPlayer';
import { HlsJsStreamControllerState } from '../HlsJsStreamControllerState';

import { createTrack, getTrack, clearCues, addCueToTrack } from '../../player/TextTrackHelper.Service';

const FRAG_BUFFER_TOLERANCE_SECOND = 4;
const MAX_CONFIG_BUFFER_SECOND = 100;

export class SubtitleHelper {
	private hlsJs: any;

	private cueParsed = (eventName: string, data: any) => this.addCueToTrack(data.cues);
	private onNonNativeTextTracksFound = (eventName: string, data: any) => this.addNonNativeTrack(data);
	private mediaAttached = () => this.onMediaAttached();

	constructor(
		private hlsPlayer: HlsPlayer
	) {
		this.hlsJs = hlsPlayer.player;
		this.hlsJs.on(this.hlsPlayer.HlsEvents.MEDIA_ATTACHED, this.mediaAttached);
		this.hlsJs.on(this.hlsPlayer.HlsEvents.CUES_PARSED, this.cueParsed);
		this.hlsJs.on(this.hlsPlayer.HlsEvents.NON_NATIVE_TEXT_TRACKS_FOUND, this.onNonNativeTextTracksFound);
	}

	public setSubtitleTrack(showSubtitle: boolean, subtitle: TextTrack): void {
		const newTrack = this.hlsJs.subtitleTracks?.find(item => item.lang === subtitle?.language);
		const newTrackId = newTrack ? newTrack.id : -1;
		if (newTrackId !== this.hlsJs.subtitleTrack) {
			this.hlsJs.subtitleTrack = newTrackId;
		}

		//this will help show and hide subtitle.
		if (showSubtitle !== this.hlsJs.subtitleDisplay) {
			this.hlsJs.subtitleDisplay = showSubtitle;
		}
	}

	public disableNativeTextTrackRender(isDisable: boolean): void {
		this.hlsJs.config.renderTextTracksNatively = !isDisable;
	}


	//workaround where subtitletrackcontroller state stuck in FRAG_LOADING
	//In that case, it wont fetch next vtt file. Setting it back to idle.
	public onSubtitleTrackLoaded(data): void {
		const controller = this.getSutitleStreamController();
		if (controller?.state === HlsJsStreamControllerState.FRAG_LOADING) {
			controller.state = HlsJsStreamControllerState.IDLE;
		}
		this.recalculateSubtitleFragCC(data.details);
	}

	//on live scenario, when playback is stopped and resumed
	//need to clean up subtitles related data.
	public onCleanupSubtitlesData(): void {
		const controller = this.getSutitleStreamController();
		if (controller) {
			controller.tracksBuffered = [];
			controller.levels?.forEach(level => {
				controller.tracksBuffered[level.id] = [];
			});

			controller.fragmentTracker?.removeFragmentsInRange(
				0,
				Number.POSITIVE_INFINITY,
				'subtitle'
			);
			controller.fragPrevious = null;
			controller.mediaBuffer = null;
		}
	}

	private onMediaAttached(): void {
		this.resetTracksBuffer(this.getSutitleStreamController(), this.hlsJs.subtitleTrack);
	}

	private resetTracksBuffer(controller: ISubtitleStreamController, trackId: string): void {
		if (controller.tracksBuffered?.[trackId]?.length) {
			controller.tracksBuffered[trackId] = [];
		}
	}


	private getSutitleStreamController(): ISubtitleStreamController {
		return this.hlsJs.networkControllers.find(controller => 'tracksBuffered' in controller);
	}

	/**
	 * This is almost a copy of hls.js SubtitleStreamController doTick method with some of our tweaks.
	 * In case of Live scenario, the timing of media from buffered and subtitle are going off. As a result
	 * Either it is fetching old subtitle which is not matching the current media time or It is fetching only last subtitle missing last but one in some cases.
	 * To Fix this, I copied binary-search, buffer-helper and fragment-finders from Hls.js to our local folder as it was giving build errors because of some other imports.
	 * Primary changes are
	 *        1. bufferLen > MAX_CONFIG_BUFFER_SECOND
	 *        2. targetBufferTime < end && (end - targetBufferTime) <= FRAG_BUFFER_TOLERANCE_SECOND
	 *        3. if (fragPrevious?.sn === fragments[fragLen - 2]?.sn) {
						foundFrag = fragments[fragLen - 1];
					} else if (fragPrevious?.sn === fragments[fragLen - 3]?.sn) {
						console.warn('2 segments added. pulling last but one');
						foundFrag = fragments[fragLen - 2];
					} else {
						foundFrag = fragments[fragLen - 1];
					}
	 *
	 */
	public assignSubtitleStreamDoTick(): void {
		const controller = this.getSutitleStreamController();
		controller.doTick = () => {
			if (!controller.media) {
				controller.state = State.IDLE;
				return;
			}

			if (controller.state === State.IDLE) {
				const { currentTrackId, levels } = controller;
				const track = levels?.[currentTrackId];
				if (!track || !levels.length || !track.details) {
					return;
				}
				const { config } = controller;
				const currentTime = controller.getLoadPosition();
				const bufferedInfo = BufferHelper.bufferedInfo(
					controller.tracksBuffered[controller.currentTrackId] || [],
					currentTime,
					config.maxBufferHole,
				);
				const { end: targetBufferTime, len: bufferLen } = bufferedInfo;

				const mainBufferInfo = controller.getFwdBufferInfo(
					controller.media,
					PlaylistLevelType.MAIN,
				);
				const trackDetails = track.details;
				const maxBufLen =
					controller.getMaxBufferLength(mainBufferInfo?.len) +
					trackDetails.levelTargetDuration;

				if (bufferLen > MAX_CONFIG_BUFFER_SECOND) {
					return;
				}
				const fragments = trackDetails.fragments;
				const fragLen = fragments.length;
				const end = trackDetails.edge;

				let foundFrag;
				const fragPrevious = controller.fragPrevious;
				if (targetBufferTime < end && (end - targetBufferTime) <= FRAG_BUFFER_TOLERANCE_SECOND) {
					const tolerance = config.maxFragLookUpTolerance;
					const lookupTolerance =
						targetBufferTime > end - tolerance ? 0 : tolerance;
					foundFrag = findFragmentByPTS(
						fragPrevious,
						fragments,
						Math.max(fragments[0].start, targetBufferTime),
						lookupTolerance,
					);
					if (
						!foundFrag &&
						fragPrevious &&
						fragPrevious.start < fragments[0].start
					) {
						foundFrag = fragments[0];
					}
				} else {
					const previousFragSn = fragPrevious?.sn;
					const lastFragSn = fragments[fragLen - 1]?.sn;

					if (previousFragSn && lastFragSn && (lastFragSn - previousFragSn) <= 4) {
						foundFrag = fragments?.find(frag => frag.sn === previousFragSn + 1);
					} else {
						foundFrag = fragments[fragLen - 1];
					}
				}
				if (!foundFrag) {
					return;
				}
				foundFrag = controller.mapToInitFragWhenRequired(foundFrag);
				if (foundFrag.sn !== 'initSegment') {
					// Load earlier fragment in same discontinuity to make up for misaligned playlists and cues that extend beyond end of segment
					const curSNIdx = foundFrag.sn - trackDetails.startSN;
					const prevFrag = fragments[curSNIdx - 1];
					if (
						prevFrag &&
						prevFrag.cc === foundFrag.cc &&
						controller.fragmentTracker.getState(prevFrag) === FragmentState.NOT_LOADED
					) {
						foundFrag = prevFrag;
					}
				}
				if (
					controller.fragmentTracker.getState(foundFrag) === FragmentState.NOT_LOADED
				) {
					// only load if fragment is not loaded
					controller.loadFragment(foundFrag, track, targetBufferTime);
				}
			}
		};
	}

	/**
	 * work around - when live is stopped and restarted from source
	 * mismatch happening between subtitle frag cc and playlist frag cc
	 * because subtitle playlist adding #EXT-X-DISCONTINUITY first and then EXT-X-DISCONTINUITY-SEQUENCE
	 * but playback playlist only adds #EXT-X-DISCONTINUITY and not EXT-X-DISCONTINUITY-SEQUENCE
	 */
	private recalculateSubtitleFragCC(details: any): void {
		const timeController = this.hlsPlayer.getTimelineController();
		const initPtsMaxIndex = timeController?.initPTS?.length - 1;
		const frags = details?.fragments;
		let ccChanged: boolean;

		if (initPtsMaxIndex >= 0 && frags?.length) {
			frags.forEach(frag => {
				if (frag.cc !== initPtsMaxIndex) {
					ccChanged = true;
					frag.cc = initPtsMaxIndex;
				}
			});

			if (ccChanged) {
				details.startCC = frags[0].cc;
				details.endCC = frags[frags.length - 1].cc;
			}
		}
	}

	//renderTextTracksNatively = false in hls.js then we need to add cue manually
	//This is needed primarily for Firefox and safari and dual playback scenario.
	private addCueToTrack(cues: VTTCue[]): void {
		const textTrack: TextTrack = this.hlsPlayer.videoElement.textTracks[this.hlsJs.subtitleTrack];

		if (textTrack) {
			cues.forEach(cue => addCueToTrack(textTrack, cue));
		}

	}

	private addNonNativeTrack(data: any): void {
		if (data.tracks.length) {
			data.tracks.forEach(track => {
				const textTrack = getTrack(this.hlsPlayer.videoElement, track.kind, track.label);
				if (textTrack) {
					// If the track already exists, clear its cues
					clearCues(textTrack);
				} else {
					// Create a new track if it doesn't exist
					createTrack(this.hlsPlayer.videoElement, track.kind, track.label, false, track.subtitleTrack.lang);
				}
			});
		}
	}
}
